import { notifyOnError } from '~/rage/decorators';
import { IObservable, SeenTracker, Subject } from '~/rage/utils';
import {
	IClientConfigs,
	IVehicleShopInfo,
	IVehicleShopOffer,
	IVehicleShopSpecs,
	RgbColor,
	WalletType,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { INotificationsService } from '../NotificationsService';
import { IPlayerService } from '../PlayerService';
import type { IRpcService } from '../RpcService';
import { VarsService } from '../VarsService/VarsService';
import { IVehicleShopService } from './vehicleShopServiceTypes';

export class VehicleShopService implements IVehicleShopService {
	private activeShopId = new Subject<string | null>(null);
	private shopInfo = new Subject<IVehicleShopInfo | null>(null);
	private numberPlatesRegions = new Subject<
		IClientConfigs['vehicleNumberPlates']['regions']
	>([]);
	private vehicleShopOffer = new Subject<IVehicleShopOffer | null>(null);
	private seenTracker = new SeenTracker('vehicle_shop');

	private allShopInfos = new Set<IVehicleShopInfo>();

	constructor(
		private readonly rpc: IRpcService,
		player: IPlayerService,
		private readonly backend: BackendService,
		private readonly notifications: INotificationsService,
		gameConfig: GameConfigService,
		private readonly vars: VarsService,
	) {
		this.rpc.register('vehicleShop:setActiveShopId', (shopId: string) => {
			this.activeShopId.next(shopId);
		});

		if (import.meta.env.DEV) {
			player
				.waitForCharacterSelect()
				.then(() => this.activeShopId.next('expensive'));
		}

		gameConfig.getConfigs().subscribe(this.handleGameConfigChange.bind(this));

		this.getActiveShopId().subscribe(() => this.updateShopInfo());

		this.vars
			.get<IVehicleShopOffer | null>('vehicleShop:offer', null)
			.subscribe((offer) => {
				this.vehicleShopOffer.next(offer);
			});
	}

	private handleGameConfigChange(config: Partial<IClientConfigs>) {
		if (!config || !config.vehicles) return;

		this.allShopInfos.clear();

		const {
			testDrivePriceMin,
			testDrivePriceCoef,
			testDriveDuration,
			shops: vehicleShops,
			models: vehicleModels,
		} = config.vehicles;

		for (const shop of vehicleShops) {
			const maxSpecs = {
				fuelTankCapacity: 0,
				trunkVolume: 0,
				maxSpeed: 0,
			};

			const shopVehicles = vehicleModels
				.filter((e) => e.shopId === shop.id)
				.map(
					({
						model,
						priceCash,
						priceDonate,
						name,
						fuelTankCapacity,
						fuelConsumptionRate,
						trunkVolume,
						maxSpeed,
						passengerSeats,
					}) => {
						const priceTestDrive = Math.round(
							Math.max(testDrivePriceMin, testDrivePriceCoef * priceCash),
						);

						maxSpecs.fuelTankCapacity = Math.max(
							maxSpecs.fuelTankCapacity,
							maxSpecs.fuelTankCapacity,
						);
						maxSpecs.trunkVolume = Math.max(
							maxSpecs.trunkVolume,
							maxSpecs.trunkVolume,
						);
						maxSpecs.maxSpeed = Math.max(maxSpecs.maxSpeed, maxSpeed);

						const specs: IVehicleShopSpecs = {
							fuelTankCapacity,
							fuelConsumptionRate,
							trunkVolume,
							maxSpeed,
							passengerSeats,
						};

						const tags = {
							new: this.isVehicleNew(model),
							// discount: 0, // @TODO скидки на ТС
						};

						return {
							model,
							priceCash,
							priceDonate,
							priceTestDrive,
							name,
							specs,
							tags,
						};
					},
				);
			shopVehicles.sort((a, b) => a.name.localeCompare(b.name));

			this.allShopInfos.add({
				id: shop.id,
				name: shop.name,
				title: shop.title,
				maxVehicleSpeed: maxSpecs.maxSpeed,
				maxTrunkVolume: maxSpecs.trunkVolume,
				maxFuelTankCapacity: maxSpecs.fuelTankCapacity,
				testDriveDurationSec: testDriveDuration / 1000,
				vehicles: shopVehicles,
			});
		}

		this.updateShopInfo();

		this.numberPlatesRegions.next(config.vehicleNumberPlates?.regions ?? []);
	}

	public getActiveShopId(): IObservable<string | null> {
		return this.activeShopId;
	}

	private updateShopInfo(): void {
		const shopId = this.activeShopId.getValue();
		if (shopId === null) return;

		const shopInfo =
			Array.from(this.allShopInfos).find((e) => e.id === shopId) ?? null;
		this.shopInfo.next(shopInfo);
	}

	public getShopInfo(): IObservable<IVehicleShopInfo | null> {
		return this.shopInfo;
	}

	public getNumberPlateRegions(): IObservable<
		IClientConfigs['vehicleNumberPlates']['regions']
	> {
		return this.numberPlatesRegions;
	}

	@notifyOnError
	public async buyVehicle(
		model: string,
		color: [RgbColor, RgbColor],
		wallet: WalletType,
	): Promise<void> {
		await this.backend.invoke('vehicleShop:buy', {
			model,
			bodyColor: color[0],
			interiorColor: color[1],
			wallet,
		});

		const shop = this.shopInfo.getValue();
		const vehicleName =
			shop?.vehicles.find((e) => e.model === model)?.name ?? 'N/A';
		this.notifications.showSuccess(`Вы успешно приобрели ${vehicleName}!`);
	}

	@notifyOnError
	public async testDriveVehicle(
		model: string,
		color: [RgbColor, RgbColor],
	): Promise<void> {
		return await this.rpc.invokeClient('vehicleShop:testDrive', {
			model,
			primaryColor: color[0],
			secondaryColor: color[1],
		});
	}

	@notifyOnError
	public async exitShop(): Promise<void> {
		return await this.rpc.invokeClient('vehicleShop:exitShop');
	}

	/**
	 * Отобразить превью выбранного автомобиля в игре
	 */
	@notifyOnError
	public async setPreviewVehicleModel(model: string | null): Promise<void> {
		return await this.rpc.invokeClient('vehicleShop:setPreviewVehicleModel', {
			model,
		});
	}

	public isUnseen(path: string): boolean {
		return this.seenTracker.isUnseen(path);
	}

	public markSeen(path: string): void {
		this.seenTracker.markAsSeen(path);
	}

	/**
	 * Сменить цвет превью выбранного автомобиля в игре
	 */
	@notifyOnError
	public async setPreviewVehicleColor(
		color: [RgbColor, RgbColor],
	): Promise<void> {
		return await this.rpc.invokeClient('vehicleShop:setPreviewVehicleColor', {
			color,
		});
	}

	private isVehicleNew(model: string): boolean {
		return localStorage.getItem(`seenVehicles_${model}`) !== '1';
	}

	public getVehicleShopOffer(): IObservable<IVehicleShopOffer | null> {
		return this.vehicleShopOffer;
	}
}
